import React, { useMemo, useRef, useState } from 'react';
import { useFitStore } from '../../store/useFitStore';
import type { GlobalExercise } from '../../store/types';
import { setToText, fmtSet, inferMeasure, getDefaultUnit } from '../../lib/sets';

/* ============================================================
   ExerciseMenu — the in-session "grab" surface for real-time
   logging. The picker (type-ahead) is for when the coach knows
   the name; this drawer is for browsing: filter by muscle group,
   search, and ONE TAP adds the exercise to the open section —
   prefilled with this client's last-performed sets (the
   progressive-overload reference), falling back to library
   defaults. Stays open for rapid multi-add mid-session.
============================================================ */

export interface MenuPick {
  name: string;
  setTexts: string[];
}

interface Props {
  clientId: string;
  sectionTitle: string;
  onAdd: (pick: MenuPick) => void;
  onClose: () => void;
}

const GROUPS = ['All', 'Recent', 'Chest', 'Back', 'Legs', 'Posterior', 'Shoulders', 'Arms', 'Core', 'Athletic', 'Mobility'];

interface LastDone {
  texts: string[];   // lossless payload — round-trips through parseSet on add
  labels: string[];  // unit-formatted for display ("225 lb × 10")
  date: string;      // ISO yyyy-mm-dd
}

const fmtShort = (iso: string): string => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const ExerciseMenu: React.FC<Props> = ({ clientId, sectionTitle, onAdd, onClose }) => {
  const { globalExercises, sessions } = useFitStore();
  const [q, setQ] = useState('');
  const [group, setGroup] = useState('All');
  const [flash, setFlash] = useState<string | null>(null); // id of just-added row
  const flashTimer = useRef<number | undefined>(undefined);

  // Most recent logged sets per exercise name for THIS client, across the whole
  // history — the number the coach wants in front of them to beat.
  const lastByName = useMemo(() => {
    const m: Record<string, LastDone> = {};
    sessions
      .filter((s) => s.clientId === clientId && s.status !== 'planned' && s.status !== 'missed')
      .sort((a, b) => a.date.localeCompare(b.date)) // ascending → later sessions overwrite
      .forEach((s) =>
        s.sections.forEach((sec) =>
          sec.exercises.forEach((ex) => {
            const texts = ex.sets.map(setToText).filter(Boolean);
            const measure = ex.measure ?? inferMeasure(ex.sets);
            const unit = ex.unit ?? getDefaultUnit();
            const labels = ex.sets.map((x) => fmtSet(x, measure, unit)).filter(Boolean);
            if (texts.length) m[ex.name.trim().toLowerCase()] = { texts, labels, date: s.date };
          })
        )
      );
    return m;
  }, [sessions, clientId]);

  const rows = useMemo(() => {
    const query = q.trim().toLowerCase();
    let list = globalExercises.map((ex) => ({ ex, last: lastByName[ex.name.trim().toLowerCase()] as LastDone | undefined }));
    if (group === 'Recent') list = list.filter((r) => r.last);
    else if (group !== 'All') list = list.filter((r) => r.ex.muscleGroup === group);
    if (query) list = list.filter((r) => r.ex.name.toLowerCase().includes(query));
    // The client's working set floats up: done-before first (most recent first),
    // then the rest alphabetically.
    return list
      .sort((a, b) => {
        if (!!a.last !== !!b.last) return a.last ? -1 : 1;
        if (a.last && b.last && a.last.date !== b.last.date) return b.last.date.localeCompare(a.last.date);
        return a.ex.name.localeCompare(b.ex.name);
      })
      .slice(0, 80);
  }, [globalExercises, lastByName, q, group]);

  const add = (ex: GlobalExercise, last?: LastDone) => {
    const setTexts = last
      ? [...last.texts]
      : ex.defaultSets > 0
        ? Array.from({ length: Math.min(ex.defaultSets, 6) }, () => ex.defaultReps || '')
        : [''];
    onAdd({ name: ex.name, setTexts });
    setFlash(ex.id);
    window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlash(null), 900);
  };

  return (
    <div
      className="fixed inset-0 flex justify-end"
      style={{ zIndex: 10000, background: 'rgba(0,0,0,0.45)' }}
      onClick={onClose}
    >
      <div
        className="h-full w-full max-w-[440px] bg-fit-bg-secondary border-l border-fit-line flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="p-4 border-b border-fit-line">
          <div className="flex items-center justify-between gap-3 mb-1">
            <h3 className="text-[16px] font-bold tracking-tight">Add to {sectionTitle}</h3>
            <button onClick={onClose} className="text-fit-text-secondary hover:text-white text-lg leading-none">×</button>
          </div>
          <p className="text-[11px] text-fit-muted mb-3">Tap to add — last logged sets come with it. Stays open for the next one.</p>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search exercises…"
            className="input w-full"
            autoFocus
          />
          {/* muscle-group chips */}
          <div className="flex gap-1.5 mt-3 overflow-x-auto no-scrollbar">
            {GROUPS.map((g) => (
              <button
                key={g}
                onClick={() => setGroup(g)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap transition-colors ${
                  group === g ? 'bg-white text-fit-ink' : 'bg-fit-bg-tertiary text-fit-text-secondary hover:text-white'
                }`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        {/* list */}
        <div className="flex-1 overflow-y-auto no-scrollbar">
          {rows.length === 0 ? (
            <p className="text-[13px] text-fit-muted text-center py-12">No matches — keep typing or switch group.</p>
          ) : (
            rows.map(({ ex, last }) => (
              <button
                key={ex.id}
                onClick={() => add(ex, last)}
                className="w-full text-left px-4 py-3 border-b border-fit-line hover:bg-white/[0.04] transition-colors"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold truncate">{ex.name}</p>
                    <p className="text-[11px] text-fit-muted">
                      {[ex.muscleGroup, ex.equipment].filter(Boolean).join(' · ') || '—'}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    {flash === ex.id ? (
                      <p className="text-[12px] font-bold">Added ✓</p>
                    ) : last ? (
                      <>
                        <p className="text-[11px] text-fit-text-secondary tabular-nums">{last.labels.slice(0, 3).join(' · ')}</p>
                        <p className="text-[10px] text-fit-muted">last · {fmtShort(last.date)}</p>
                      </>
                    ) : (
                      <p className="text-[11px] text-fit-muted">＋</p>
                    )}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ExerciseMenu;
